import {cva, type VariantProps} from 'class-variance-authority'
import {HTMLAttributes} from 'react'

import {cn} from '@utilities/helpers'

const badgeVariants = cva(
    'inline-flex items-center justify-center gap-1 wrap-break-word font-medium -outline-offset-1 [&_svg]:size-3',
    {
        variants: {
            variant: {
                primary:
                    'bg-primary-50 text-primary-700 outline outline-1 outline-primary-200 [&_svg]:fill-primary-500',
                neutral:
                    'bg-neutral-50 text-neutral-700 outline outline-1 outline-neutral-200 [&_svg]:fill-neutral-500',
                orange: 'bg-orange-50 text-orange-700 outline outline-1 outline-orange-200 [&_svg]:fill-orange-500',
                blue: 'bg-blue-50 text-blue-700 outline outline-1 outline-blue-200 [&_svg]:fill-blue-500'
            },
            size: {
                sm: 'text-xs',
                md: 'text-sm',
                lg: 'text-sm'
            },
            shape: {
                rectangle: '',
                roundedRectangle: '',
                square: 'shrink-0',
                circle: 'shrink-0'
            }
        },
        compoundVariants: [
            // ---------- sm ----------
            {shape: 'rectangle', size: 'sm', class: 'min-h-5.5 rounded-md px-2 py-0.5'},
            {shape: 'roundedRectangle', size: 'sm', class: 'min-h-5.5 rounded-[11px] px-2 py-0.5'},
            {shape: 'square', size: 'sm', class: 'size-5.5 rounded-md'},
            {shape: 'circle', size: 'sm', class: 'size-5.5 rounded-[11px]'},
            // ---------- md ----------
            {shape: 'rectangle', size: 'md', class: 'min-h-6 rounded-md px-2.5 py-0.5'},
            {shape: 'roundedRectangle', size: 'md', class: 'min-h-6 rounded-xl px-2.5 py-0.5'},
            {shape: 'square', size: 'md', class: 'size-6 rounded-md'},
            {shape: 'circle', size: 'md', class: 'size-6 rounded-xl'},
            // ---------- lg ----------
            {shape: 'rectangle', size: 'lg', class: 'min-h-7 rounded-md px-3 py-1'},
            {shape: 'roundedRectangle', size: 'lg', class: 'min-h-7 rounded-[14px] px-2 py-1'},
            {shape: 'square', size: 'lg', class: 'size-7 rounded-md'},
            {shape: 'circle', size: 'lg', class: 'size-7 rounded-[14px]'}
        ],
        defaultVariants: {
            variant: 'neutral',
            size: 'md',
            shape: 'roundedRectangle'
        }
    }
)

export type BadgeProps = HTMLAttributes<HTMLDivElement> & VariantProps<typeof badgeVariants>

export const Badge = ({
    className,
    variant = 'neutral',
    size = 'md',
    shape = 'roundedRectangle',
    ...rest
}: BadgeProps) => {
    return <div className={cn(badgeVariants({variant, size, shape, className}))} data-slot="badge" {...rest} />
}

export {badgeVariants}
